import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { AuthorMiniProfile } from "../components/AuthorMiniProfile";
import { fetchProfile } from "./dmApi";

// Lightweight context so any deep child (e.g. VoicePost author name) can
// pop up the author mini-profile modal without prop-drilling through five
// route layers. The provider owns the modal state and mounts the modal
// once at the root. `useMemo` on the value prevents a fresh object on
// every render — otherwise every consumer re-renders unnecessarily.

const MiniProfileCtx = createContext({ open: () => {} });
const DELETED_PROFILE_NAME = "Profilo eliminato";

export function MiniProfileProvider({ children, onOpenMessages }) {
  const [target, setTarget] = useState(null);
  const open = useCallback(async (identityId) => {
    if (!identityId) return;
    try {
      const profile = await fetchProfile(identityId);
      if (profile?.displayName === DELETED_PROFILE_NAME) return;
      setTarget(identityId);
    } catch {
      // Preserve the existing error handling in AuthorMiniProfile for
      // unavailable normal profiles, while deleted profiles remain inert.
      setTarget(identityId);
    }
  }, []);
  const close = useCallback(() => setTarget(null), []);
  const startDm = useCallback(
    (identityId) => {
      close();
      if (typeof onOpenMessages === "function") onOpenMessages(identityId);
    },
    [close, onOpenMessages],
  );
  const ctxValue = useMemo(() => ({ open }), [open]);
  return (
    <MiniProfileCtx.Provider value={ctxValue}>
      {children}
      {target && (
        <AuthorMiniProfile
          identityId={target}
          onClose={close}
          onStartDm={startDm}
        />
      )}
    </MiniProfileCtx.Provider>
  );
}

export function useMiniProfile() {
  return useContext(MiniProfileCtx);
}
